import {ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, Input, OnInit} from '@angular/core';
import {SelectOptions} from '@aviary/store/common/common.types';
import {MatRadioButton, MatRadioChange, MatRadioGroup} from '@angular/material/radio';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ControlRef} from '@aviary/forms/fields/form-fields.types';
import {getExtendedControlFrom} from '@aviary/forms/form.helpers';
import {ExtendedFormGroup} from '@aviary/forms/extensions/extended-form-group/extended-form-group';
import {ExtendedFormControl} from '@aviary/forms/extensions/extended-form-control/extended-form-control';
import {ReactiveFormsModule} from '@angular/forms';
import {NgClass} from '@angular/common';
import {TruncateDirective} from '@aviary/directives/truncate.directive';
import {provideControlRefTokenFor} from '@aviary/providers/control-ref.provider';

@Component({
  selector: 'mx-radio-group-form-field',
  template: `
    <div [formGroup]="formGroup" class="mx-radio-group-form-field-body flex justify-start"
         [ngClass]="vertical ? 'flex-col items-start' : 'flex-row items-center'"
         [class.mx-radio-group-form-field-body-vertical]="vertical" [class.m-0]="forTable">
      @if (label) {
        <p class="mx-radio-group-label">{{ label }}</p>
      }

      <mat-radio-group color="primary" [formControlName]="fieldName" [value]="control.value" (change)="onChange($event)" class="flex" [class.w-full]="forTable"
                       [ngClass]="vertical ? 'flex-col items-start' : 'flex-row place-content-evenly items-center'">
        <!-- 
          Right padding is set to visually balance with 10px radio button padding, which improves group centering.  (20x20 icon centered in a 40x40 space.) 
        -->
        @for (option of options; track option.value) {
          <mat-radio-button [value]="option.value" class="pe-2.5" [class.w-full]="forTable">
            <span mxTruncate>{{ option.displayName }}</span>
          </mat-radio-button>
        }
      </mat-radio-group>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideControlRefTokenFor(RadioGroupFormFieldComponent)],
  imports: [ReactiveFormsModule, NgClass, MatRadioGroup, MatRadioButton, TruncateDirective]
})
export class RadioGroupFormFieldComponent<ValueType> implements OnInit, ControlRef {

  @Input() public fieldName: string;
  @Input() public formGroup: ExtendedFormGroup;
  @Input() public options: SelectOptions<ValueType>;
  @Input() public enabled = true;
  @Input() public vertical: boolean = false;
  @Input() public forTable: boolean = false;
  @Input() public label: string;

  public control: ExtendedFormControl;  // It's tempting to rename this to "formControl", but that's a reserved name and causes confusing errors.  So don't.
  public selectedValue: ValueType;

  private readonly changeDetector: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  public ngOnInit(): void {
    this.control = getExtendedControlFrom(this.formGroup, this.fieldName);

    this.control.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        this.selectedValue = value;
        this.changeDetector.markForCheck();
      });
  }

  public onChange(event: MatRadioChange): void {
    this.control.patchValue(event.value);
  }
}
